import * as THREE from 'three';
import { XHRFactory } from './XHRFactory.js';
import { Volume } from './utils/Volume.js';
import { Profile } from './utils/Profile.js';
import { Measure } from './utils/Measure.js';
import { PolygonClipVolume } from './utils/PolygonClipVolume.js';
import { PointCloudOctreeNode } from './PointCloudOctree.js';

export class Utils {
    static extractAxis(name) {
        const match = name.match(/[xyz]/);
        if (match) {
            return match[0];
        }
        return null;
    }
    static async loadShapefileFeatures(file, callback) {
        let features = [];

        let handleFinish = () => {
            callback(features);
        };

        let source = await shapefile.open(file);

        while (true) {
            let result = await source.read();

            if (result.done) {
                handleFinish();
                break;
            }

            if (result.value && result.value.type === 'Feature' && result.value.geometry !== undefined) {
                features.push(result.value);
            }
        }
    }

    static toString(value) {
        if (value.x != null) {
            return value.x.toFixed(2) + ', ' + value.y.toFixed(2) + ', ' + value.z.toFixed(2);
        } else {
            return '' + value + '';
        }
    }

    static normalizeURL(url) {
        let u = new URL(url);

        return u.protocol + '//' + u.hostname + u.pathname.replace(/\/+/g, '/');
    }

    static pathExists(url) {
        let req = XHRFactory.createXMLHttpRequest();
        req.open('GET', url, false);
        req.send(null);
        if (req.status !== 200) {
            return false;
        }
        return true;
    }

    static debugSphere(parent, position, scale, color) {
        let geometry = new THREE.SphereGeometry(1, 8, 8);
        let material;

        if (color !== undefined) {
            material = new THREE.MeshBasicMaterial({ color: color });
        } else {
            material = new THREE.MeshNormalMaterial();
        }
        let sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(position);
        sphere.scale.set(scale, scale, scale);
        parent.add(sphere);

        return sphere;
    }

    static debugLine(parent, start, end, color) {
        let material = new THREE.LineBasicMaterial({ color: color });
        let geometry = new THREE.BufferGeometry();

        const p1 = new THREE.Vector3(0, 0, 0);
        const p2 = end.clone().sub(start);

        geometry.setFromPoints([p1, p2]);

        let tl = new THREE.Line(geometry, material);
        tl.position.copy(start);

        parent.add(tl);

        let line = {
            node: tl,
            set: (start, end) => {
                const positions = geometry.attributes.position.array;
                positions[0] = 0;
                positions[1] = 0;
                positions[2] = 0;
                const diff = end.clone().sub(start);
                positions[3] = diff.x;
                positions[4] = diff.y;
                positions[5] = diff.z;
                geometry.attributes.position.needsUpdate = true;
                tl.position.copy(start);
            },
        };

        return line;
    }

    static debugCircle(parent, center, radius, normal, color) {
        let material = new THREE.LineBasicMaterial({ color: color });

        let geometry = new THREE.BufferGeometry();

        let n = 32;
        const vertices = [];
        for (let i = 0; i <= n; i++) {
            let u0 = 2 * Math.PI * (i / n);
            let u1 = (2 * Math.PI * (i + 1)) / n;

            let p0 = new THREE.Vector3(Math.cos(u0), Math.sin(u0), 0);

            let p1 = new THREE.Vector3(Math.cos(u1), Math.sin(u1), 0);

            vertices.push(p0, p1);
        }
        geometry.setFromPoints(vertices);

        let tl = new THREE.Line(geometry, material);
        tl.position.copy(center);
        tl.scale.set(radius, radius, radius);

        parent.add(tl);
    }

    static isGoogleTiles(name) {
        return name.includes('GoogleTiles');
    }

    static debugBox(parent, box, transform = new THREE.Matrix4(), color = 0xffff00) {
        let vertices = [
            [box.min.x, box.min.y, box.min.z],
            [box.min.x, box.min.y, box.max.z],
            [box.min.x, box.max.y, box.min.z],
            [box.min.x, box.max.y, box.max.z],

            [box.max.x, box.min.y, box.min.z],
            [box.max.x, box.min.y, box.max.z],
            [box.max.x, box.max.y, box.min.z],
            [box.max.x, box.max.y, box.max.z],
        ].map((v) => new THREE.Vector3(...v));

        let edges = [
            [0, 4],
            [4, 5],
            [5, 1],
            [1, 0],
            [2, 6],
            [6, 7],
            [7, 3],
            [3, 2],
            [0, 2],
            [4, 6],
            [5, 7],
            [1, 3],
        ];

        let center = box.getCenter(new THREE.Vector3());

        let centroids = [
            { position: [box.min.x, center.y, center.z], color: 0xff0000 },
            { position: [box.max.x, center.y, center.z], color: 0x880000 },

            { position: [center.x, box.min.y, center.z], color: 0x00ff00 },
            { position: [center.x, box.max.y, center.z], color: 0x008800 },

            { position: [center.x, center.y, box.min.z], color: 0x0000ff },
            { position: [center.x, center.y, box.max.z], color: 0x000088 },
        ];

        for (let vertex of vertices) {
            let pos = vertex.clone().applyMatrix4(transform);

            Utils.debugSphere(parent, pos, 0.1, 0xff0000);
        }

        for (let edge of edges) {
            let start = vertices[edge[0]].clone().applyMatrix4(transform);
            let end = vertices[edge[1]].clone().applyMatrix4(transform);

            Utils.debugLine(parent, start, end, color);
        }

        for (let centroid of centroids) {
            let pos = new THREE.Vector3(...centroid.position).applyMatrix4(transform);

            Utils.debugSphere(parent, pos, 0.1, centroid.color);
        }
    }

    static debugPlane(parent, plane, size = 1, color = 0x0000ff) {
        let planehelper = new THREE.PlaneHelper(plane, size, color);

        parent.add(planehelper);
    }

    /**
     * adapted from mhluska at https://github.com/mrdoob/three.js/issues/1561
     */
    static computeTransformedBoundingBox(box, transform) {
        let vertices = [
            new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
            new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
            new THREE.Vector3(box.max.x, box.min.y, box.min.z).applyMatrix4(transform),
            new THREE.Vector3(box.min.x, box.max.y, box.min.z).applyMatrix4(transform),
            new THREE.Vector3(box.min.x, box.min.y, box.max.z).applyMatrix4(transform),
            new THREE.Vector3(box.min.x, box.max.y, box.max.z).applyMatrix4(transform),
            new THREE.Vector3(box.max.x, box.max.y, box.min.z).applyMatrix4(transform),
            new THREE.Vector3(box.max.x, box.min.y, box.max.z).applyMatrix4(transform),
            new THREE.Vector3(box.max.x, box.max.y, box.max.z).applyMatrix4(transform),
        ];

        let boundingBox = new THREE.Box3();
        boundingBox.setFromPoints(vertices);

        return boundingBox;
    }

    /**
     * add separators to large numbers
     *
     * @param nStr
     * @returns
     */
    static addCommas(nStr) {
        nStr += '';
        let x = nStr.split('.');
        let x1 = x[0];
        let x2 = x.length > 1 ? '.' + x[1] : '';
        let rgx = /(\d+)(\d{3})/;
        while (rgx.test(x1)) {
            x1 = x1.replace(rgx, '$1' + ',' + '$2');
        }
        return x1 + x2;
    }

    static removeCommas(str) {
        return str.replace(/,/g, '');
    }

    /**
     * create worker from a string
     *
     * code from http://stackoverflow.com/questions/10343913/how-to-create-a-web-worker-from-a-string
     */
    static createWorker(code) {
        let blob = new Blob([code], { type: 'application/javascript' });
        let worker = new Worker(URL.createObjectURL(blob));

        return worker;
    }

    static hideAllPointclouds(pointclouds) {
        pointclouds.forEach((pointcloud) => {
            pointcloud.visible = false;
        });
    }

    static showAllPointclouds(pointclouds) {
        pointclouds.forEach((pointcloud) => {
            pointcloud.visible = true;
        });
    }

    static moveTo(scene, endPosition, endTarget, skipHidingMesh, viewToUpdate, rewriteCameraData, fragments) {
        if (window.viewer && window.viewer.orbitControls && !skipHidingMesh)
            window.viewer.orbitControls.dispatchEvent({ type: 'hideMeshes' });
        let view = scene.view;
        let camera = scene.getActiveCamera();
        let animationDuration = 500;
        let easing = TWEEN.Easing.Quartic.Out;

        {
            // animate camera position
            let tween = new TWEEN.Tween(view.position).to(endPosition, animationDuration);
            tween.easing(easing);
            tween.start();
            tween.onComplete(async () => {
                if (fragments) await fragments.core.update(true);
                if (window.viewer && window.viewer.orbitControls && !skipHidingMesh)
                    window.viewer.orbitControls.dispatchEvent({ type: 'showMeshes' });
            });
        }

        {
            // animate camera target
            let camTargetDistance = camera.position.distanceTo(endTarget);
            let target = new THREE.Vector3().addVectors(
                camera.position,
                camera.getWorldDirection(new THREE.Vector3()).clone().multiplyScalar(camTargetDistance)
            );
            let tween = new TWEEN.Tween(target).to(endTarget, animationDuration);
            tween.easing(easing);
            tween.onUpdate(() => {
                view.lookAt(target);
            });
            tween.onComplete(() => {
                view.lookAt(target);
                if (viewToUpdate && rewriteCameraData) {
                    rewriteCameraData(viewToUpdate);
                }
            });
            tween.start();
        }
    }

    static loadSkybox(path) {
        let parent = new THREE.Object3D('skybox_root');

        let camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 100000);
        camera.up.set(0, 0, 1);
        let scene = new THREE.Scene();

        let format = '.jpg';
        let urls = [
            path + 'px' + format,
            path + 'nx' + format,
            path + 'py' + format,
            path + 'ny' + format,
            path + 'pz' + format,
            path + 'nz' + format,
        ];

        let materialArray = [];
        {
            for (let i = 0; i < 6; i++) {
                let material = new THREE.MeshBasicMaterial({
                    map: null,
                    side: THREE.BackSide,
                    depthTest: false,
                    depthWrite: false,
                    color: 0x424556,
                });

                materialArray.push(material);

                let loader = new THREE.TextureLoader();
                loader.load(
                    urls[i],
                    function loaded(texture) {
                        material.map = texture;
                        material.needsUpdate = true;
                        material.color.setHex(0xffffff);
                    },
                    function progress(xhr) {
                        // console.log( (xhr.loaded / xhr.total * 100) + '% loaded' );
                    },
                    function error(xhr) {
                        console.log('An error happened', xhr);
                    }
                );
            }
        }

        let skyGeometry = new THREE.BoxGeometry(700, 700, 700);
        let skybox = new THREE.Mesh(skyGeometry, materialArray);

        scene.add(skybox);

        scene.traverse((n) => (n.frustumCulled = false));

        // z up
        scene.rotation.x = Math.PI / 2;

        parent.children.push(camera);
        camera.parent = parent;

        return { camera, scene, parent };
    }

    static createGrid(width, length, spacing, color) {
        let material = new THREE.LineBasicMaterial({
            color: color || 0x888888,
        });

        let geometry = new THREE.BufferGeometry();
        const vertices = [];
        for (let i = 0; i <= length; i++) {
            vertices.push(new THREE.Vector3(-(spacing * width) / 2, i * spacing - (spacing * length) / 2, 0));
            vertices.push(new THREE.Vector3(+(spacing * width) / 2, i * spacing - (spacing * length) / 2, 0));
        }

        for (let i = 0; i <= width; i++) {
            vertices.push(new THREE.Vector3(i * spacing - (spacing * width) / 2, -(spacing * length) / 2, 0));
            vertices.push(new THREE.Vector3(i * spacing - (spacing * width) / 2, +(spacing * length) / 2, 0));
        }
        geometry.setFromPoints(vertices);

        let line = new THREE.LineSegments(geometry, material);
        line.receiveShadow = true;
        return line;
    }

    static createBackgroundTexture(width, height) {
        function gauss(x, y) {
            return (1 / (2 * Math.PI)) * Math.exp(-(x * x + y * y) / 2);
        }

        // map.magFilter = THREE.NearestFilter;
        let size = width * height;
        let data = new Uint8Array(3 * size);

        let chroma = [1, 1.5, 1.7];
        let max = gauss(0, 0);

        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                let u = 2 * (x / width) - 1;
                let v = 2 * (y / height) - 1;

                let i = x + width * y;
                let d = gauss(2 * u, 2 * v) / max;
                let r = (Math.random() + Math.random() + Math.random()) / 3;
                r = (d * 0.5 + 0.5) * r * 0.03;
                r = r * 0.4;

                // d = Math.pow(d, 0.6);

                data[3 * i + 0] = 255 * (d / 15 + 0.05 + r) * chroma[0];
                data[3 * i + 1] = 255 * (d / 15 + 0.05 + r) * chroma[1];
                data[3 * i + 2] = 255 * (d / 15 + 0.05 + r) * chroma[2];
            }
        }

        let texture = new THREE.DataTexture(data, width, height, THREE.RGBFormat);
        texture.needsUpdate = true;

        return texture;
    }

    static getGroupName(url) {
        if (!url) {
            return null;
        }

        let decodedUrl = decodeURIComponent(url);

        let urlParts = decodedUrl.split('/');

        let panoramaIndex = urlParts.indexOf('panoramaImages');
        if (panoramaIndex === -1 || panoramaIndex >= urlParts.length - 1) {
            return null;
        }

        let groupNameWithExt = urlParts[panoramaIndex + 1].split('?')[0];

        let groupName = groupNameWithExt;

        return groupName;
    }

    static pickFromRay(pointcloud, viewer, camera, ray, params = {}) {
        const renderer = viewer.renderer;
        const pRenderer = viewer.pRenderer;
        performance.mark('pick-start');
        const getVal = (a, b) => (a !== undefined ? a : b);
        const pickWindowSize = getVal(params.pickWindowSize, 65);
        const size = renderer.getSize(new THREE.Vector2());
        const width = Math.ceil(getVal(params.width, size.width));
        const height = Math.ceil(getVal(params.height, size.height));
        const pointSizeType = getVal(params.pointSizeType, pointcloud.material.pointSizeType);
        const pointSize = getVal(params.pointSize, pointcloud.material.size);
        const nodes =
            pointcloud.root instanceof PointCloudOctreeNode
                ? pointcloud.nodesOnRay([pointcloud.root, ...pointcloud.visibleNodes], ray)
                : pointcloud.nodesOnRay(pointcloud.visibleNodes, ray);
        if (nodes.length === 0) {
            return null;
        }
        if (!pointcloud.pickState) {
            const scene = new THREE.Scene();
            const material = new Potree.PointCloudMaterial();
            material.activeAttributeName = 'indices';
            const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
                minFilter: THREE.LinearFilter,
                magFilter: THREE.NearestFilter,
                format: THREE.RGBAFormat,
            });
            pointcloud.pickState = { renderTarget, material, scene };
        }
        const pickState = pointcloud.pickState;
        const pickMaterial = pickState.material;
        pickMaterial.pointSizeType = pointSizeType;
        pickMaterial.shape = Potree.PointShape.PARABOLOID;
        pickMaterial.uniforms.uFilterReturnNumberRange.value = pointcloud.material.uniforms.uFilterReturnNumberRange.value;
        pickMaterial.uniforms.uFilterNumberOfReturnsRange.value = pointcloud.material.uniforms.uFilterNumberOfReturnsRange.value;
        pickMaterial.uniforms.uFilterGPSTimeClipRange.value = pointcloud.material.uniforms.uFilterGPSTimeClipRange.value;
        pickMaterial.uniforms.uFilterPointSourceIDClipRange.value = pointcloud.material.uniforms.uFilterPointSourceIDClipRange.value;
        pickMaterial.activeAttributeName = 'indices';
        pickMaterial.size = pointSize;
        pickMaterial.uniforms.minSize.value = pointcloud.material.uniforms.minSize.value;
        pickMaterial.uniforms.maxSize.value = pointcloud.material.uniforms.maxSize.value;
        pickMaterial.classification = pointcloud.material.classification;
        pickMaterial.recomputeClassification();
        if (params.pickClipped) {
            pickMaterial.clipBoxes = pointcloud.material.clipBoxes;
            pickMaterial.uniforms.clipBoxes = pointcloud.material.uniforms.clipBoxes;
            pickMaterial.clipTask =
                pointcloud.material.clipTask === Potree.ClipTask.HIGHLIGHT ? Potree.ClipTask.NONE : pointcloud.material.clipTask;
            pickMaterial.clipMethod = pointcloud.material.clipMethod;
        } else {
            pickMaterial.clipBoxes = [];
        }
        pointcloud.updateMaterial(pickMaterial, nodes, camera, renderer);
        pickState.renderTarget.setSize(width, height);
        const pixelPos = new THREE.Vector2(width / 2, height / 2);
        const gl = renderer.getContext();
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(
            parseInt(pixelPos.x - (pickWindowSize - 1) / 2),
            parseInt(pixelPos.y - (pickWindowSize - 1) / 2),
            parseInt(pickWindowSize),
            parseInt(pickWindowSize)
        );
        renderer.state.buffers.depth.setTest(pickMaterial.depthTest);
        renderer.state.buffers.depth.setMask(pickMaterial.depthWrite);
        renderer.state.setBlending(THREE.NoBlending);
        renderer.setRenderTarget(pickState.renderTarget);
        gl.clearColor(0, 0, 0, 0);
        renderer.clear(true, true, true);
        const tmp = pointcloud.material;
        pointcloud.material = pickMaterial;
        pRenderer.renderOctree(pointcloud, nodes, camera, pickState.renderTarget);
        pointcloud.material = tmp;
        const clamp = (number, min, max) => Math.min(Math.max(min, number), max);
        const x = parseInt(clamp(pixelPos.x - (pickWindowSize - 1) / 2, 0, width));
        const y = parseInt(clamp(pixelPos.y - (pickWindowSize - 1) / 2, 0, height));
        const w = parseInt(Math.min(x + pickWindowSize, width) - x);
        const h = parseInt(Math.min(y + pickWindowSize, height) - y);
        const pixelCount = w * h;
        const buffer = new Uint8Array(4 * pixelCount);
        gl.readPixels(x, y, pickWindowSize, pickWindowSize, gl.RGBA, gl.UNSIGNED_BYTE, buffer);
        renderer.setRenderTarget(null);
        renderer.state.reset();
        renderer.setScissorTest(false);
        gl.disable(gl.SCISSOR_TEST);
        const pixels = buffer;
        const ibuffer = new Uint32Array(buffer.buffer);
        let hits = [];
        for (let u = 0; u < pickWindowSize; u++) {
            for (let v = 0; v < pickWindowSize; v++) {
                const offset = u + v * pickWindowSize;
                const distance = Math.pow(u - (pickWindowSize - 1) / 2, 2) + Math.pow(v - (pickWindowSize - 1) / 2, 2);
                const pcIndex = pixels[4 * offset + 3];
                pixels[4 * offset + 3] = 0;
                const pIndex = ibuffer[offset];
                if (!(pcIndex === 0 && pIndex === 0) && pcIndex !== undefined && pIndex !== undefined) {
                    const hit = { pIndex, pcIndex, distanceToCenter: distance };
                    if (params.all) {
                        hits.push(hit);
                    } else {
                        if (hits.length > 0) {
                            if (distance < hits[0].distanceToCenter) {
                                hits[0] = hit;
                            }
                        } else {
                            hits.push(hit);
                        }
                    }
                }
            }
        }
        for (let hit of hits) {
            const point = {};
            if (!nodes[hit.pcIndex]) {
                return null;
            }
            const node = nodes[hit.pcIndex];
            const pc = node.sceneNode;
            const geometry = node.geometryNode.geometry;
            for (let attributeName in geometry.attributes) {
                const attribute = geometry.attributes[attributeName];
                if (attributeName === 'position') {
                    const x = attribute.array[3 * hit.pIndex + 0];
                    const y = attribute.array[3 * hit.pIndex + 1];
                    const z = attribute.array[3 * hit.pIndex + 2];
                    const position = new THREE.Vector3(x, y, z);
                    position.applyMatrix4(pc.matrixWorld);
                    point[attributeName] = position;
                } else if (attributeName === 'indices') {
                } else {
                    let values = attribute.array.slice(attribute.itemSize * hit.pIndex, attribute.itemSize * (hit.pIndex + 1));
                    if (attribute.potree) {
                        const { scale, offset } = attribute.potree;
                        values = values.map((v) => v / scale + offset);
                    }
                    point[attributeName] = values;
                }
            }
            hit.point = point;
        }
        performance.mark('pick-end');
        performance.measure('pick', 'pick-start', 'pick-end');
        if (params.all) {
            return hits.map((hit) => hit.point);
        } else {
            return hits.length === 0 ? null : hits[0].point;
        }
    }

    static getRayPointCloudIntersection(origin, direction, camera, viewer, pointclouds, params = {}) {
        const renderer = viewer.renderer;
        const size = renderer.getSize(new THREE.Vector2());
        const tempCam = new THREE.PerspectiveCamera(camera.fov, size.width / size.height, camera.near, camera.far);
        tempCam.position.copy(origin);
        tempCam.lookAt(origin.clone().add(direction));
        tempCam.updateMatrixWorld();
        const ray = new THREE.Ray(origin, direction);
        const filteredPointClouds = params.pickInvisible ? pointclouds : pointclouds.filter((pc) => pc.visible);
        let selectedPointcloud = null;
        let closestDistance = Infinity;
        let closestIntersection = null;
        let closestPoint = null;
        for (const pointcloud of filteredPointClouds) {
            const point = this.pickFromRay(pointcloud, viewer, tempCam, ray, params);
            if (!point) {
                continue;
            }
            const distance = origin.distanceTo(point.position);
            if (distance < closestDistance) {
                closestDistance = distance;
                selectedPointcloud = pointcloud;
                closestIntersection = point.position;
                closestPoint = point;
            }
        }
        return selectedPointcloud
            ? {
                  location: closestIntersection,
                  distance: closestDistance,
                  pointcloud: selectedPointcloud,
                  point: closestPoint,
              }
            : null;
    }

    static getMousePointCloudIntersection(mouse, camera, viewer, pointclouds, params = {}) {
        const renderer = viewer.renderer;

        const nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        const pickParams = {
            x: mouse.x,
            y: renderer.domElement.clientHeight - mouse.y,
            pickClipped: params.pickClipped || undefined,
        };

        const filteredPointClouds = params.pickInvisible ? pointclouds : pointclouds.filter((pc) => pc.visible);

        let selectedPointcloud = null;
        let closestDistance = Infinity;
        let closestIntersection = null;
        let closestPoint = null;

        for (const pointcloud of filteredPointClouds) {
            const point = pointcloud.pick(viewer, camera, raycaster.ray, pickParams);

            if (!point) continue;

            const distance = camera.position.distanceTo(point.position);

            if (distance < closestDistance) {
                closestDistance = distance;
                selectedPointcloud = pointcloud;
                closestIntersection = point.position;
                closestPoint = point;
            }
        }

        return selectedPointcloud
            ? {
                  location: closestIntersection,
                  distance: closestDistance,
                  pointcloud: selectedPointcloud,
                  point: closestPoint,
              }
            : null;
    }

    // AABB is intentionally used over per-triangle intersection to avoid jitter on DWG geometry.
    static getMouseIfcIntersection(mouse, camera, renderer, ifcHelper) {
        const domElement = renderer.domElement;
        const ndcX = (mouse.x / domElement.clientWidth) * 2 - 1;
        const ndcY = -(mouse.y / domElement.clientHeight) * 2 + 1;
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
        const ray = raycaster.ray;

        const candidates = ifcHelper.scene.children.filter((c) => c.visible && c.constructor.name !== 'TilesGroup');
        const box = new THREE.Box3();
        const bestPoint = candidates.reduce((best, child) => {
            box.setFromObject(child);
            if (box.isEmpty() || !isFinite(box.min.x) || !isFinite(box.max.x)) return best;
            const pt = ray.intersectBox(box, new THREE.Vector3());
            if (!pt) return best;
            const dist = ray.origin.distanceTo(pt);
            return !best || dist < best.dist ? { point: pt.clone(), dist } : best;
        }, null)?.point;

        return bestPoint ? { location: bestPoint } : null;
    }

    static getIntersectionWithFocusedImage(mouse, camera, viewer, meshes) {
        let renderer = viewer.renderer;

        let nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };

        let raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        // Get intersections with the provided mesh
        let intersects = raycaster.intersectObjects(meshes, true);
        if (intersects.length > 0) {
            let closestIntersection = intersects[0];
            return {
                location: closestIntersection.point,
                distance: closestIntersection.distance,
                mesh: closestIntersection.object,
                face: closestIntersection.face,
                faceIndex: closestIntersection.faceIndex,
            };
        } else {
            return null;
        }
    }

    static getMeshesIntersectionWithVolumes(mouse, camera, viewer, volumes, params = {}) {
        let renderer = viewer.renderer;

        let nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };

        let raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        let filteredVolumes = volumes.filter((volume) => {
            if (params.pickInvisible) {
                return volume.uuid !== params.newVolumeMesh && volume.clip === false;
            } else {
                return volume.uuid !== params.newVolumeMesh && volume.visible === true && volume.clip === false;
            }
        });

        // Extract the actual box/sphere meshes and create a map back to parent volumes
        // This avoids raycasting LineSegments (frame) and other non-mesh children
        let meshToVolume = new Map();
        let meshes = [];
        for (let volume of filteredVolumes) {
            // BoxVolume uses 'box', SphereVolume uses 'sphere', PlaneVolume uses 'box'
            let mesh = volume.box || volume.sphere;
            if (mesh) {
                meshes.push(mesh);
                meshToVolume.set(mesh, volume);
            }
        }

        // Single raycast call against all box/sphere meshes (non-recursive since meshes are direct)
        let intersects = raycaster.intersectObjects(meshes, false);

        if (intersects.length > 0) {
            // intersectObjects already returns sorted by distance, first is closest
            let closestIntersection = intersects[0];
            let volume = meshToVolume.get(closestIntersection.object);

            return {
                location: closestIntersection.point,
                distance: closestIntersection.distance,
                volumeMesh: volume, // Return the parent Volume, not the internal mesh
                face: closestIntersection.face,
                faceIndex: closestIntersection.faceIndex,
            };
        } else {
            return null;
        }
    }

    static getMeshesIntersectionWithLibraryMesh(mouse, camera, viewer, meshes, params = {}) {
        let renderer = viewer.renderer;

        let nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };

        let raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        let filteredMeshes = meshes
            .filter((volume) => {
                if (params.pickInvisible) {
                    return volume.uuid !== params.newVolumeMesh;
                } else {
                    return volume.uuid !== params.newVolumeMesh && volume.visible === true;
                }
            })
            .map((volume) => volume);

        let intersects = raycaster.intersectObjects(filteredMeshes, true);
        if (intersects.length > 0) {
            let closestIntersection = intersects[0];
            for (let intersect of intersects) {
                if (intersect.distance < closestIntersection.distance) {
                    closestIntersection = intersect;
                }
            }

            const parentObject = this.findObjectWithSceneParent(closestIntersection.object);

            return {
                location: closestIntersection.point,
                distance: closestIntersection.distance,
                libraryMesh: parentObject,
                face: closestIntersection.face,
                faceIndex: closestIntersection.faceIndex,
            };
        } else {
            return null;
        }
    }

    static getMeshesIntersectionWithCesium(mouse, toScene, cesiumViewer, facilityCords) {
        if (window[cesiumViewer] && window[cesiumViewer].scene) {
            var scene = window[cesiumViewer].scene;

            var pickRay = scene.camera.getPickRay(new Cesium.Cartesian2(mouse.x, mouse.y));

            var cartesian = scene.globe.pick(pickRay, scene);

            if (!cartesian) {
                return null;
            }

            var cartographic = Cesium.Cartographic.fromCartesian(cartesian);

            var longitude = Cesium.Math.toDegrees(cartographic.longitude);
            var latitude = Cesium.Math.toDegrees(cartographic.latitude);
            var height = cartographic.height;

            const intersection = toScene.forward([longitude, latitude]);

            const offset = {
                x: intersection[0] - facilityCords[0],
                y: intersection[1] - facilityCords[1],
                z: height,
            };

            const point = new THREE.Vector3(offset.x, offset.y, offset.z);

            return {
                location: point,
            };
        }
    }

    static processChildrenArray(groups) {
        let meshes = [];
        groups.forEach((group) => {
            if (group.type === 'Group' || group instanceof THREE.Object3D) {
                meshes = meshes.concat(Utils.getAllMeshes(group));
            }
        });
        return meshes;
    }

    static getAllMeshes(object, firstChild = true) {
        let meshes = [];
        if (object.children) {
            object.children.forEach((child, index) => {
                if (child.type === 'Mesh' && !(object instanceof THREE.Object3D && firstChild && index === 0)) {
                    meshes.push(child);
                }
                if ((child.type === 'Group' || child instanceof THREE.Object3D) && child.children.length > 0) {
                    meshes = meshes.concat(Utils.getAllMeshes(child, false));
                }
            });
        }
        return meshes;
    }

    static getMousePositionOnViewer(mouse, camera, viewer, depth = 10000) {
        let renderer = viewer.renderer;
        let nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };
        let raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        let sphere = new THREE.Sphere(new Vector3(0, 0, 0), depth);

        let intersectionPoint = new THREE.Vector3();
        let intersects = raycaster.ray.intersectsSphere(sphere);

        if (intersects) {
            raycaster.ray.at(intersects, intersectionPoint);
        }

        return { location: intersectionPoint };
    }

    static getMouseMeshIntersection(mouse, camera, viewer, children, params = {}) {
        let renderer = viewer.renderer;

        let nmouse = {
            x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
            y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
        };

        let pickParams = {};

        if (params.pickClipped) {
            pickParams.pickClipped = params.pickClipped;
        }

        pickParams.x = mouse.x;
        pickParams.y = renderer.domElement.clientHeight - mouse.y;

        let raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(nmouse, camera);

        let meshes = Utils.processChildrenArray(children);

        let intersects = raycaster.intersectObjects(meshes);
        if (intersects.length > 0) {
            let closestIntersection = intersects[0];
            for (let intersect of intersects) {
                if (intersect.distance < closestIntersection.distance) {
                    closestIntersection = intersect;
                }
            }
            return {
                location: closestIntersection.point,
                distance: closestIntersection.distance,
                mesh: closestIntersection.object,
                face: closestIntersection.face,
                faceIndex: closestIntersection.faceIndex,
            };
        } else {
            return null;
        }
    }

    static getImageName(url) {
        if (!url) {
            return null;
        }
        let decodedUrl = decodeURIComponent(url);
        const [, extractedFileName] = decodedUrl.split('panoramaImages/');
        return extractedFileName || null;
    }

    static pixelsArrayToImage(pixels, width, height) {
        let canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        let context = canvas.getContext('2d');

        pixels = new pixels.constructor(pixels);

        for (let i = 0; i < pixels.length; i++) {
            pixels[i * 4 + 3] = 255;
        }

        let imageData = context.createImageData(width, height);
        imageData.data.set(pixels);
        context.putImageData(imageData, 0, 0);

        let img = new Image();
        img.src = canvas.toDataURL();
        // img.style.transform = "scaleY(-1)";

        return img;
    }

    static pixelsArrayToDataUrl(pixels, width, height) {
        let canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        let context = canvas.getContext('2d');

        pixels = new pixels.constructor(pixels);

        for (let i = 0; i < pixels.length; i++) {
            pixels[i * 4 + 3] = 255;
        }

        let imageData = context.createImageData(width, height);
        imageData.data.set(pixels);
        context.putImageData(imageData, 0, 0);

        let dataURL = canvas.toDataURL();

        return dataURL;
    }

    static pixelsArrayToCanvas(pixels, width, height) {
        let canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        let context = canvas.getContext('2d');

        pixels = new pixels.constructor(pixels);

        //for (let i = 0; i < pixels.length; i++) {
        //	pixels[i * 4 + 3] = 255;
        //}

        // flip vertically
        let bytesPerLine = width * 4;
        for (let i = 0; i < parseInt(height / 2); i++) {
            let j = height - i - 1;

            let lineI = pixels.slice(i * bytesPerLine, i * bytesPerLine + bytesPerLine);
            let lineJ = pixels.slice(j * bytesPerLine, j * bytesPerLine + bytesPerLine);
            pixels.set(lineJ, i * bytesPerLine);
            pixels.set(lineI, j * bytesPerLine);
        }

        let imageData = context.createImageData(width, height);
        imageData.data.set(pixels);
        context.putImageData(imageData, 0, 0);

        return canvas;
    }

    static removeListeners(dispatcher, type) {
        if (dispatcher._listeners === undefined) {
            return;
        }

        if (dispatcher._listeners[type]) {
            delete dispatcher._listeners[type];
        }
    }

    static mouseToRay(mouse, camera, width, height) {
        let normalizedMouse = {
            x: (mouse.x / width) * 2 - 1,
            y: -(mouse.y / height) * 2 + 1,
        };

        const ray = new THREE.Ray();

        if (camera.isPerspectiveCamera) {
            let vector = new THREE.Vector3(normalizedMouse.x, normalizedMouse.y, 0.5);
            let origin = camera.position.clone();
            vector.unproject(camera);
            let direction = vector.sub(origin).normalize();
            ray.origin.copy(origin);
            ray.direction.copy(direction);
        } else if (camera.isOrthographicCamera) {
            let vector = new THREE.Vector3(normalizedMouse.x, normalizedMouse.y, 0);
            vector.unproject(camera);
            const direction = new THREE.Vector3(0, 0, -1).transformDirection(camera.matrixWorld);
            ray.origin.copy(vector);
            ray.direction.copy(direction);
        }

        return ray;
    }

    static projectedRadius(radius, camera, distance, screenWidth, screenHeight) {
        if (camera instanceof THREE.OrthographicCamera) {
            return Utils.projectedRadiusOrtho(radius, camera.projectionMatrix, screenWidth, screenHeight);
        } else if (camera instanceof THREE.PerspectiveCamera) {
            return Utils.projectedRadiusPerspective(radius, (camera.fov * Math.PI) / 180, distance, screenHeight);
        } else {
            throw new Error('invalid parameters');
        }
    }

    static projectedRadiusPerspective(radius, fov, distance, screenHeight) {
        let projFactor = 1 / Math.tan(fov / 2) / distance;
        projFactor = (projFactor * screenHeight) / 2;

        return radius * projFactor;
    }

    static projectedRadiusOrtho(radius, proj, screenWidth, screenHeight) {
        let p1 = new THREE.Vector4(0);
        let p2 = new THREE.Vector4(radius);

        p1.applyMatrix4(proj);
        p2.applyMatrix4(proj);
        p1 = new THREE.Vector3(p1.x, p1.y, p1.z);
        p2 = new THREE.Vector3(p2.x, p2.y, p2.z);
        p1.x = (p1.x + 1.0) * 0.5 * screenWidth;
        p1.y = (p1.y + 1.0) * 0.5 * screenHeight;
        p2.x = (p2.x + 1.0) * 0.5 * screenWidth;
        p2.y = (p2.y + 1.0) * 0.5 * screenHeight;
        return p1.distanceTo(p2);
    }

    static topView(camera, node) {
        camera.position.set(0, 1, 0);
        camera.rotation.set(-Math.PI / 2, 0, 0);
        camera.zoomTo(node, 1);
    }

    static frontView(camera, node) {
        camera.position.set(0, 0, 1);
        camera.rotation.set(0, 0, 0);
        camera.zoomTo(node, 1);
    }

    static leftView(camera, node) {
        camera.position.set(-1, 0, 0);
        camera.rotation.set(0, -Math.PI / 2, 0);
        camera.zoomTo(node, 1);
    }

    static rightView(camera, node) {
        camera.position.set(1, 0, 0);
        camera.rotation.set(0, Math.PI / 2, 0);
        camera.zoomTo(node, 1);
    }

    static findClosestGpsTime(target, viewer) {
        const start = performance.now();

        const nodes = [];
        for (const pc of viewer.scene.pointclouds) {
            nodes.push(pc.root);

            for (const child of pc.root.children) {
                if (child) {
                    nodes.push(child);
                }
            }
        }

        let closestNode = null;
        let closestIndex = Infinity;
        let closestDistance = Infinity;
        let closestValue = 0;

        for (const node of nodes) {
            const isOkay = node.geometryNode != null && node.geometryNode.geometry != null && node.sceneNode != null;

            if (!isOkay) {
                continue;
            }

            let geometry = node.geometryNode.geometry;
            let gpsTime = geometry.attributes['gps-time'];
            let range = gpsTime.potree.range;

            for (let i = 0; i < gpsTime.array.length; i++) {
                let value = gpsTime.array[i];
                value = value * (range[1] - range[0]) + range[0];
                const distance = Math.abs(target - value);

                if (distance < closestDistance) {
                    closestIndex = i;
                    closestDistance = distance;
                    closestValue = value;
                    closestNode = node;
                    //console.log("found a closer one: " + value);
                }
            }
        }

        const geometry = closestNode.geometryNode.geometry;
        const position = new THREE.Vector3(
            geometry.attributes.position.array[3 * closestIndex + 0],
            geometry.attributes.position.array[3 * closestIndex + 1],
            geometry.attributes.position.array[3 * closestIndex + 2]
        );

        position.applyMatrix4(closestNode.sceneNode.matrixWorld);

        const end = performance.now();
        const duration = end - start;
        console.log(`duration: ${duration.toFixed(3)}ms`);

        return {
            node: closestNode,
            index: closestIndex,
            position: position,
        };
    }

    /**
     *
     * 0: no intersection
     * 1: intersection
     * 2: fully inside
     */
    static frustumSphereIntersection(frustum, sphere) {
        let planes = frustum.planes;
        let center = sphere.center;
        let negRadius = -sphere.radius;

        let minDistance = Number.MAX_VALUE;

        for (let i = 0; i < 6; i++) {
            let distance = planes[i].distanceToPoint(center);

            if (distance < negRadius) {
                return 0;
            }

            minDistance = Math.min(minDistance, distance);
        }

        return minDistance >= sphere.radius ? 2 : 1;
    }

    // code taken from three.js
    // ImageUtils - generateDataTexture()
    static generateDataTexture(width, height, color) {
        let size = width * height;
        let data = new Uint8Array(4 * width * height);

        let r = Math.floor(color.r * 255);
        let g = Math.floor(color.g * 255);
        let b = Math.floor(color.b * 255);

        for (let i = 0; i < size; i++) {
            data[i * 3] = r;
            data[i * 3 + 1] = g;
            data[i * 3 + 2] = b;
        }

        let texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;

        return texture;
    }

    // from http://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
    static getParameterByName(name) {
        name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
        let regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        let results = regex.exec(document.location.search);
        return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    static setParameter(name, value) {
        // value = encodeURIComponent(value);

        name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
        let regex = new RegExp('([\\?&])(' + name + '=([^&#]*))');
        let results = regex.exec(document.location.search);

        let url = window.location.href;
        if (results === null) {
            if (window.location.search.length === 0) {
                url = url + '?';
            } else {
                url = url + '&';
            }

            url = url + name + '=' + value;
        } else {
            let newValue = name + '=' + value;
            url = url.replace(results[2], newValue);
        }
        window.history.replaceState({}, '', url);
    }

    static createChildAABB(aabb, index) {
        let min = aabb.min.clone();
        let max = aabb.max.clone();
        let size = new THREE.Vector3().subVectors(max, min);

        if ((index & 0b0001) > 0) {
            min.z += size.z / 2;
        } else {
            max.z -= size.z / 2;
        }

        if ((index & 0b0010) > 0) {
            min.y += size.y / 2;
        } else {
            max.y -= size.y / 2;
        }

        if ((index & 0b0100) > 0) {
            min.x += size.x / 2;
        } else {
            max.x -= size.x / 2;
        }

        return new THREE.Box3(min, max);
    }

    // see https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
    static clipboardCopy(text) {
        let textArea = document.createElement('textarea');

        textArea.style.position = 'fixed';
        textArea.style.top = 0;
        textArea.style.left = 0;

        textArea.style.width = '2em';
        textArea.style.height = '2em';

        textArea.style.padding = 0;

        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';

        textArea.style.background = 'transparent';

        textArea.value = text;

        document.body.appendChild(textArea);

        textArea.select();

        try {
            let success = document.execCommand('copy');
            if (success) {
                console.log('copied text to clipboard');
            } else {
                console.log('copy to clipboard failed');
            }
        } catch (err) {
            console.log('error while trying to copy to clipboard');
        }

        document.body.removeChild(textArea);
    }

    static getMeasurementIcon(measurement) {
        if (measurement instanceof Measure) {
            if (measurement.showDistances && !measurement.showArea && !measurement.showAngles) {
                return `${Potree.resourcePath}/icons/distance.svg`;
            } else if (measurement.showDistances && measurement.showArea && !measurement.showAngles) {
                return `${Potree.resourcePath}/icons/area.svg`;
            } else if (measurement.maxMarkers === 1) {
                return `${Potree.resourcePath}/icons/point.svg`;
            } else if (!measurement.showDistances && !measurement.showArea && measurement.showAngles) {
                return `${Potree.resourcePath}/icons/angle.png`;
            } else if (measurement.showHeight) {
                return `${Potree.resourcePath}/icons/height.svg`;
            } else {
                return `${Potree.resourcePath}/icons/distance.svg`;
            }
        } else if (measurement instanceof Profile) {
            return `${Potree.resourcePath}/icons/profile.svg`;
        } else if (measurement instanceof Volume) {
            return `${Potree.resourcePath}/icons/volume.svg`;
        } else if (measurement instanceof PolygonClipVolume) {
            return `${Potree.resourcePath}/icons/clip-polygon.svg`;
        } else if (measurement.isPlane) {
            return `${Potree.resourcePath}/icons/clip-plane.svg`;
        }
    }

    static lineToLineIntersection(P0, P1, P2, P3) {
        const P = [P0, P1, P2, P3];

        const d = (m, n, o, p) => {
            let result =
                (P[m].x - P[n].x) * (P[o].x - P[p].x) + (P[m].y - P[n].y) * (P[o].y - P[p].y) + (P[m].z - P[n].z) * (P[o].z - P[p].z);

            return result;
        };

        const mua =
            (d(0, 2, 3, 2) * d(3, 2, 1, 0) - d(0, 2, 1, 0) * d(3, 2, 3, 2)) /
            /**-----------------------------------------------------------------**/ (d(1, 0, 1, 0) * d(3, 2, 3, 2) -
                d(3, 2, 1, 0) * d(3, 2, 1, 0));

        const mub = (d(0, 2, 3, 2) + mua * d(3, 2, 1, 0)) / /**--------------------------------------**/ d(3, 2, 3, 2);

        const P01 = P1.clone().sub(P0);
        const P23 = P3.clone().sub(P2);

        const Pa = P0.clone().add(P01.multiplyScalar(mua));
        const Pb = P2.clone().add(P23.multiplyScalar(mub));

        const center = Pa.clone().add(Pb).multiplyScalar(0.5);

        return center;
    }

    static computeCircleCenter(A, B, C) {
        const AB = B.clone().sub(A);
        const AC = C.clone().sub(A);

        const N = AC.clone().cross(AB).normalize();

        const ab_dir = AB.clone().cross(N).normalize();
        const ac_dir = AC.clone().cross(N).normalize();

        const ab_origin = A.clone().add(B).multiplyScalar(0.5);
        const ac_origin = A.clone().add(C).multiplyScalar(0.5);

        const P0 = ab_origin;
        const P1 = ab_origin.clone().add(ab_dir);

        const P2 = ac_origin;
        const P3 = ac_origin.clone().add(ac_dir);

        const center = Utils.lineToLineIntersection(P0, P1, P2, P3);

        return center;

        // Potree.Utils.debugLine(viewer.scene.scene, P0, P1, 0x00ff00);
        // Potree.Utils.debugLine(viewer.scene.scene, P2, P3, 0x0000ff);

        // Potree.Utils.debugSphere(viewer.scene.scene, center, 0.03, 0xff00ff);

        // const radius = center.distanceTo(A);
        // Potree.Utils.debugCircle(viewer.scene.scene, center, radius, new THREE.Vector3(0, 0, 1), 0xff00ff);
    }

    static getNorthVec(p1, distance, projection) {
        if (projection) {
            // if there is a projection, transform coordinates to WGS84
            // and compute angle to north there

            proj4.defs('pointcloud', projection);
            const transform = proj4('pointcloud', 'WGS84');

            const llP1 = transform.forward(p1.toArray());
            let llP2 = transform.forward([p1.x, p1.y + distance]);
            const polarRadius = Math.sqrt((llP2[0] - llP1[0]) ** 2 + (llP2[1] - llP1[1]) ** 2);
            llP2 = [llP1[0], llP1[1] + polarRadius];

            const northVec = transform.inverse(llP2);

            return new THREE.Vector3(...northVec, p1.z).sub(p1);
        } else {
            // if there is no projection, assume [0, 1, 0] as north direction

            const vec = new THREE.Vector3(0, 1, 0).multiplyScalar(distance);

            return vec;
        }
    }

    static computeAzimuth(p1, p2, projection) {
        let azimuth = 0;

        if (projection) {
            // if there is a projection, transform coordinates to WGS84
            // and compute angle to north there

            let transform;

            if (projection.includes('EPSG')) {
                transform = proj4(projection, 'WGS84');
            } else {
                proj4.defs('pointcloud', projection);
                transform = proj4('pointcloud', 'WGS84');
            }

            const llP1 = transform.forward(p1.toArray());
            const llP2 = transform.forward(p2.toArray());
            const dir = [llP2[0] - llP1[0], llP2[1] - llP1[1]];
            azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
        } else {
            // if there is no projection, assume [0, 1, 0] as north direction

            const dir = [p2.x - p1.x, p2.y - p1.y];
            azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
        }

        // make clockwise
        azimuth = -azimuth;

        return azimuth;
    }

    static async loadScript(url) {
        return new Promise((resolve) => {
            const element = document.getElementById(url);

            if (element) {
                resolve();
            } else {
                const script = document.createElement('script');

                script.id = url;

                script.onload = () => {
                    resolve();
                };
                script.src = url;

                document.body.appendChild(script);
            }
        });
    }

    static createSvgGradient(scheme) {
        // this is what we are creating:
        //
        //<svg width="1em" height="3em"  xmlns="http://www.w3.org/2000/svg">
        //	<defs>
        //		<linearGradient id="gradientID" gradientTransform="rotate(90)">
        //		<stop offset="0%"  stop-color="rgb(93, 78, 162)" />
        //		...
        //		<stop offset="100%"  stop-color="rgb(157, 0, 65)" />
        //		</linearGradient>
        //	</defs>
        //
        //	<rect width="100%" height="100%" fill="url('#myGradient')" stroke="black" stroke-width="0.1em"/>
        //</svg>

        const gradientId = `${Math.random()}_${Date.now()}`;

        const svgn = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgn, 'svg');
        svg.setAttributeNS(null, 'width', '2em');
        svg.setAttributeNS(null, 'height', '3em');

        {
            // <defs>
            const defs = document.createElementNS(svgn, 'defs');

            const linearGradient = document.createElementNS(svgn, 'linearGradient');
            linearGradient.setAttributeNS(null, 'id', gradientId);
            linearGradient.setAttributeNS(null, 'gradientTransform', 'rotate(90)');

            for (let i = scheme.length - 1; i >= 0; i--) {
                const stopVal = scheme[i];
                const percent = parseInt(100 - stopVal[0] * 100);
                const [r, g, b] = stopVal[1].toArray().map((v) => parseInt(v * 255));

                const stop = document.createElementNS(svgn, 'stop');
                stop.setAttributeNS(null, 'offset', `${percent}%`);
                stop.setAttributeNS(null, 'stop-color', `rgb(${r}, ${g}, ${b})`);

                linearGradient.appendChild(stop);
            }

            defs.appendChild(linearGradient);
            svg.appendChild(defs);
        }

        const rect = document.createElementNS(svgn, 'rect');
        rect.setAttributeNS(null, 'width', `100%`);
        rect.setAttributeNS(null, 'height', `100%`);
        rect.setAttributeNS(null, 'fill', `url("#${gradientId}")`);
        rect.setAttributeNS(null, 'stroke', `black`);
        rect.setAttributeNS(null, 'stroke-width', `0.1em`);

        svg.appendChild(rect);

        return svg;
    }

    static async waitAny(promises) {
        return new Promise((resolve) => {
            promises.map((promise) => {
                promise.then(() => {
                    resolve();
                });
            });
        });
    }

    static findObjectWithSceneParent(mesh) {
        if (mesh.parent === null) {
            return null;
        }
        if (mesh.parent.type === 'Scene') {
            return mesh;
        }

        return this.findObjectWithSceneParent(mesh.parent);
    }

    static truncateAfterLastHyphen(value) {
        const lastIndex = value.lastIndexOf('-');
        return lastIndex !== -1 ? value.substring(0, lastIndex) : value;
    }

    static calculateSphericalDistance(sphere, point1, point2) {
        const radius = sphere.geometry.parameters.radius;
        const center = sphere.position;

        const vector1 = new THREE.Vector3().subVectors(point1, center);
        const vector2 = new THREE.Vector3().subVectors(point2, center);

        vector1.normalize();
        vector2.normalize();

        const angle = vector1.angleTo(vector2);

        const distanceOnSphere = radius * angle;

        return distanceOnSphere;
    }
}

Utils.screenPass = new (function () {
    this.screenScene = new THREE.Scene();
    this.screenQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2, 1));
    this.screenQuad.material.depthTest = true;
    this.screenQuad.material.depthWrite = true;
    this.screenQuad.material.transparent = true;
    this.screenScene.add(this.screenQuad);
    this.camera = new THREE.Camera();

    this.render = function (renderer, material, target) {
        this.screenQuad.material = material;

        if (typeof target === 'undefined') {
            renderer.render(this.screenScene, this.camera);
        } else {
            renderer.render(this.screenScene, this.camera, target);
        }
    };
})();
